import { useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import moment from 'moment';
import findLast from 'ramda/src/findLast';
import isEmpty from 'ramda/src/isEmpty';
import isNil from 'ramda/src/isNil';
import * as Animations from '@components/@core/Animations';
import CheckBox from '@components/@core/CheckBox';
import Icon from '@components/@core/Icon';
import Rating from '@components/@core/Rating';
import EditDropdown from '@components/EditDropdown';
import { QuotaTooltip, RatingTooltip } from '@components/ScorecardToolTips';
import { QuotaCalcs } from '~/services/config';
import { arrangeQuota, getUnit } from '~/services/util';
import { QUOTA_TYPES } from '~/services/constants';

const calcDetail = (quota, now) => {
    const { quota_calculation } = quota.data;
    const dateStart = moment(now.start).startOf('month').format('YYYY-MM-DD');
    const dateEnd = moment(now.end).endOf('month').format('YYYY-MM-DD');
    const actual = findLast((x) => moment(x.actual_at).isBetween(dateStart, dateEnd))(quota.actuals);

    let calculation = null;
    if (quota_calculation == QuotaCalcs.MONTH || quota_calculation == QuotaCalcs.QUARTER) {
        if (quota.data?.calculation?.months && actual) calculation = quota.data.calculation.months[actual.actual_at];
    } else if (quota_calculation === QuotaCalcs.TOTAL) {
        calculation = quota.data?.calculation?.total;
    }

    const actualValue = actual?.actual || 0;
    const data = actual?.data;
    const starRating = calculation?.star_rating && typeof calculation.star_rating === 'number' ? calculation.star_rating : 0;
    return { starRating, actualValue, data, incomplete: isNil(actual) };
};

const Header = ({ title, editable = false }) => (
    <div className="d-md-flex d-none ai-center border-bottom pt-3 pb-2">
        <div className="include d-flex-1">
            <span className="dsl-m12">Include</span>
        </div>
        <div className="d-flex-10 ps-4">
            <span className="dsl-m12">{title}</span>
        </div>
        <div className="d-flex-3">
            <span className="dsl-m12">Comments</span>
        </div>
        <div className="d-flex-2">
            <span className="dsl-m12">Target</span>
        </div>
        <div className="d-flex-2">
            <span className="dsl-m12">Actual</span>
        </div>
        <div className="d-flex-3 pe-4">
            <span className="dsl-m12 ps-3 ms-2">Score</span>
        </div>
        {editable && <div className="d-flex-1" />}
    </div>
);

const List = ({ editable = false, quotas, now, limit = 6, onModal = () => {} }) => {
    const [visible, setVisible] = useState(-1);

    return (
        <>
            {quotas?.map((quota, index) => {
                const { starRating, actualValue, data, incomplete } = calcDetail(quota, now);
                if (limit < 3 && isNil(actualValue)) return null;
                if (starRating < limit) {
                    const stars = arrangeQuota(quota.data.star_values, quota.data.quota_direction);
                    const unit = QUOTA_TYPES[quota.data?.quota_value ?? 0];
                    return (
                        <div className="d-flex ssm-column align-items-md-center border-bottom cursor-pointer py-4" key={`q${index}`}>
                            <div className="include mb-1 mb-md-0 d-flex-1">
                                <div className="mobile-label d-md-none">
                                    <label className="mb-0 dsl-m12">Include</label>
                                </div>
                                <CheckBox 
                                    className="ms-auto" 
                                    size="tiny" 
                                    id={quota.id} 
                                    checked={actualValue !== null} 
                                    readOnly={!editable}
                                    onChange={editable ? () => {} : undefined}
                                />
                            </div>
                            <div className="d-flex-10 ps-4 d-flex ai-center mb-1 mb-md-0">
                                <div className="mobile-label d-md-none">
                                    <label className="mb-0 dsl-m12">Goal</label>
                                </div>
                                <OverlayTrigger placement="top" overlay={QuotaTooltip(quota.data.description, quota.name)}>
                                    <span className="dsl-b14">{quota.name}</span>
                                </OverlayTrigger>
                            </div>
                            <div className="d-flex-3 position-relative d-flex d-md-block ai-center">
                                <div className="mobile-label d-md-none">
                                    <label className="mb-0 dsl-m12">Comments</label>
                                </div>
                                <Icon
                                    active={!isNil(data)}
                                    name="fas fa-comment"
                                    size={14}
                                    colors={['#376caf', '#969faa']}
                                    onMouseEnter={() => setVisible(index)}
                                    onMouseLeave={() => setVisible(-1)}
                                />
                                <Animations.Popup className="comment-popup" enter={10} exit={0} opened={!isNil(data) && visible == index}>
                                    <div className="close">
                                        <Icon name="fas fa-xmark cursor-pointer" color="#676767" size={16} onClick={() => setVisible(-1)} />
                                    </div>
                                    {data &&
                                        data.comments.map((item, index) => (
                                            <div key={`h${index}`}>
                                                <p className="dsl-m12 mb-1">By {item.name}</p>
                                                <p className="dsl-b14 mt-2 mb-0">{item.comment}</p>
                                            </div>
                                        ))}
                                </Animations.Popup>
                            </div>
                            <div className="d-flex-2 d-flex d-md-block ai-center mb-1 mb-md-0">
                                <div className="mobile-label d-md-none">
                                    <label className="mb-0 dsl-m12">Target</label>
                                </div>
                                <span className="dsl-b14">{getUnit(stars[0]?.value, unit)}</span>
                            </div>
                            <div className="d-flex-2 d-flex d-md-block ai-center mb-1 mb-md-0">
                                <div className="mobile-label d-md-none">
                                    <label className="mb-0 dsl-m12">Actual</label>
                                </div>
                                <div className="dsl-b14 cursor-pointer" onClick={() => onModal(quota)}>
                                    {isNil(actualValue) ? 'NA' : getUnit(actualValue, unit)}
                                </div>
                            </div>
                            <OverlayTrigger placement="top" overlay={RatingTooltip(quota)}>
                                <div className="d-flex-3 d-flex ms-md-3 mb-1 mb-md-0 text-end d-md-block">
                                    <div className="mobile-label d-md-none">
                                        <label className="mb-0 dsl-m12">Score</label>
                                    </div>
                                    {incomplete ? <span className="dsl-b14">Incomplete</span> : <>{actualValue ? <Rating score={starRating} /> : <span className="dsl-b14">NA</span>}</>}
                                </div>
                            </OverlayTrigger>
                            {editable && (
                                <div className="d-flex-1">
                                    <EditDropdown options={['Edit Actuals']} onChange={(e) => onModal(e)} />
                                </div>
                            )}
                        </div>
                    );
                }
                return null;
            })}
        </>
    );
};

const Total = ({ quotas, now, limit = 6 }) => {
    let quotaLength = 0;

    let totalRating = 0;
    if (!isEmpty(quotas)) {
        quotas.forEach((quota) => {
            const { starRating, actualValue } = calcDetail(quota, now);
            if (actualValue && !isNil(starRating) && starRating < limit) {
                totalRating += starRating;
                quotaLength += 1;
            }
        });
    }

    return (
        <div className="d-flex ai-center jc-end ssm-jc-center mt-md-4">
            <div className="include" />
            <div className="d-flex-1" />
            <div className="d-flex jc-end">
                <span className="dsl-b14 bold">Monthly rating:</span>
            </div>
            <div className="d-flex ms-3">
                <Rating size="medium" score={quotaLength === 0 ? 0 : (totalRating / quotaLength).toFixed(1)} />
            </div>
        </div>
    );
};

export { Header, List, Total };
